<script setup lang="ts">
import { SelectGroup, SelectLabel } from 'reka-ui';
import { computed, useAttrs } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';
import type { VueClass } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';

import { injectSelectContext } from './context';
import KaSelectOption from './KaSelectOption.vue';
import { selectGroupLabelVariants, selectGroupVariants } from './select-variants';
import type { KaSelectGroupProps } from './types';

export type { KaSelectGroupProps } from './types';

const props = withDefaults(defineProps<KaSelectGroupProps>(), {
  hideLabel: false,
  options: undefined,
});

const attrs = useAttrs();

const selectContext = injectSelectContext();
const selectDataTestid = computed(() => selectContext?.dataTestid?.value);

// reka's SelectGroup renders role="group", generates its own id, and points aria-labelledby at the
// SelectLabel automatically (it overrides any id/aria-labelledby we pass), so we only supply test
// ids, styling, and the label/options content.
// Pass a computed (not a one-time string) so the group's test id tracks runtime changes to the
// parent select's dataTestid/id, keeping the deliberately-reactive injected context intact.
const { dataTestid, suffix } = useDataTestid(
  computed(() => `${selectDataTestid.value}-group-${props.label}`),
);
</script>
<template>
  <SelectGroup
    :data-testid="dataTestid"
    :class="cnMerge(selectGroupVariants(), attrs.class as VueClass)"
  >
    <SelectLabel
      :data-testid="suffix('label')"
      :class="cnMerge(selectGroupLabelVariants({ hideLabel: props.hideLabel }), props.labelClasses)"
    >
      {{ props.label }}
    </SelectLabel>

    <slot v-if="!props.options" />

    <!--
      Key on the option's value (plain data), not the whole option — its left/right icons may be Vue
      VNodes that JSON.stringify can't serialize (circular structures throw) and are expensive to
      stringify.
    -->
    <KaSelectOption
      v-for="option in props.options"
      v-else
      v-bind="option"
      :key="`option-${JSON.stringify(option.value)}`"
    />
  </SelectGroup>
</template>
